import type { Vec2, Ring } from '../../../geo/types';
import type { LinearRGB } from '../../../core/color';
import type { WallRun, Gate } from '../../../data/types';
import type { MeshBuilder, VertexStyle, Frame, ExaggerationStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { layoutToWorld, worldToLayout } from '../../../core/coords';
import { polylineLength } from '../../../geo/polyline';
import { horizontalCap, orientedFace, planPoint } from '../solids';
import { offsetPolyline, pathIntervals, strokeRing, subPath } from '../planGeometry';
import { FORTIFICATION_ROOF } from './roofs';
import { NO_WATER, type WaterTest } from './moat';
import { buildWallPiece, citySide, wallPieceRing, wallSection, type WallPiece } from './wallArcade';
import type { OutwardField } from './wallFacing';
import { gateBlock, type GateBlock } from './gates';
import { abutGateBlocks } from './wallAbutment';

export interface WallBuildResult {
  readonly triangles: number;
  /** Plan outlines of the built wall pieces, in layout coordinates. */
  readonly pieces: readonly Ring[];
}

interface WallStyles {
  readonly wall: VertexStyle;
  readonly niche: VertexStyle;
  readonly roof: VertexStyle;
}

export interface WallSetting {
  /** The water beside the wall: its arcade stands on the other side. */
  readonly isWater: WaterTest;
  /** Colour of the backs of the niches under the arches. */
  readonly nicheColor: LinearRGB;
  /** The direction from the wall to its moat (wallFacing.ts); without it the wall takes the whole
   *  exaggeration of the painter view wherever it turns. */
  readonly outward?: OutwardField;
}

const WALL = {
  /** Wall ends reach this far into a gate block so no crack opens at the joint. */
  gateOverlap: 0.3,
  minPieceLength: 0.5,
} as const;

function toBlockLocal(p: Vec2, block: GateBlock): Vec2 {
  const c = Math.cos(block.heading);
  const s = Math.sin(block.heading);
  const dx = p[0] - block.centre[0];
  const dy = p[1] - block.centre[1];
  return [dx * c + dy * s, -dx * s + dy * c];
}

/** Liang-Barsky: the parameter range of a + t·d (t in 0..1) inside the box |x| <= half. */
function clipToBox(a: Vec2, d: Vec2, half: Vec2): [number, number] | null {
  let t0 = 0;
  let t1 = 1;
  for (const axis of [0, 1] as const) {
    if (Math.abs(d[axis]) < 1e-12) {
      if (Math.abs(a[axis]) > half[axis]) return null;
      continue;
    }
    const ta = (-half[axis] - a[axis]) / d[axis];
    const tb = (half[axis] - a[axis]) / d[axis];
    t0 = Math.max(t0, Math.min(ta, tb));
    t1 = Math.min(t1, Math.max(ta, tb));
    if (t0 >= t1) return null;
  }
  return [t0, t1];
}

function localBlocks(gates: readonly Gate[], anchor: Vec2): GateBlock[] {
  return gates
    .filter((g) => g.kind === 'land')
    .map(gateBlock)
    .map((b) => ({ ...b, centre: [b.centre[0] - anchor[0], b.centre[1] - anchor[1]] as Vec2 }));
}

/** Arc-length intervals of the path that run through a land gate's block. */
function gateCuts(path: readonly Vec2[], gates: readonly Gate[]): [number, number][] {
  const cuts: [number, number][] = [];
  for (const gate of gates.filter((g) => g.kind === 'land')) {
    const block = gateBlock(gate);
    const half: Vec2 = [block.half[0] - WALL.gateOverlap, block.half[1] - WALL.gateOverlap];
    let walked = 0;
    for (let i = 0; i < path.length - 1; i++) {
      const a = toBlockLocal(path[i]!, block);
      const b = toBlockLocal(path[i + 1]!, block);
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const hit = clipToBox(a, [b[0] - a[0], b[1] - a[1]], half);
      if (hit) cuts.push([walked + hit[0] * len, walked + hit[1] * len]);
      walked += len;
    }
  }
  return cuts;
}

function slope(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  eave: readonly [Vec2, Vec2],
  ridge: readonly [Vec2, Vec2],
  y: readonly [number, number]
): void {
  const [ea, eb] = eave;
  const [ra, rb] = ridge;
  const du = rb[0] - ra[0];
  const dv = rb[1] - ra[1];
  const side = Math.sign((ea[0] - ra[0]) * -dv + (ea[1] - ra[1]) * du);
  const points = [
    planPoint(ea[0], y[0], ea[1]),
    planPoint(eb[0], y[0], eb[1]),
    planPoint(rb[0], y[1], rb[1]),
    planPoint(ra[0], y[1], ra[1]),
  ];
  orientedFace(builder, style, frame, points, [-dv * side, 1, -du * side]);
}

function gableEnd(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  corners: readonly [Vec2, Vec2, Vec2],
  y: readonly [number, number],
  outward: Vec2
): void {
  const [l, r, ridge] = corners;
  const points = [
    planPoint(l[0], y[0], l[1]),
    planPoint(r[0], y[0], r[1]),
    planPoint(ridge[0], y[1], ridge[1]),
  ];
  orientedFace(builder, style, frame, points, [outward[0], 0, -outward[1]]);
}

function unitDirection(from: Vec2, to: Vec2): Vec2 {
  const len = Math.hypot(to[0] - from[0], to[1] - from[1]);
  return [(to[0] - from[0]) / len, (to[1] - from[1]) / len];
}

/** A closed gabled roof over the wall walk, mitred at every bend. */
function coveredWalk(
  builder: MeshBuilder,
  styles: WallStyles,
  frame: Frame,
  points: readonly Vec2[],
  eaveY: number,
  halfWidth: number
): void {
  const { rise, overhang } = FORTIFICATION_ROOF.coveredWalk;
  const reach = halfWidth + overhang;
  const y = [eaveY, eaveY + rise] as const;
  const left = offsetPolyline(points, reach);
  const right = offsetPolyline(points, -reach);
  for (let i = 0; i < points.length - 1; i++) {
    const ridge = [points[i]!, points[i + 1]!] as const;
    slope(builder, styles.roof, frame, [left[i]!, left[i + 1]!], ridge, y);
    slope(builder, styles.roof, frame, [right[i]!, right[i + 1]!], ridge, y);
  }
  const last = points.length - 1;
  const back = unitDirection(points[1]!, points[0]!);
  const ahead = unitDirection(points[last - 1]!, points[last]!);
  gableEnd(builder, styles.wall, frame, [left[0]!, right[0]!, points[0]!], y, back);
  gableEnd(builder, styles.wall, frame, [left[last]!, right[last]!, points[last]!], y, ahead);
  horizontalCap(builder, styles.wall, frame, strokeRing(points, reach), eaveY, false);
}

function wallExaggeration(outward: OutwardField | undefined): ExaggerationStyle {
  if (!outward) return { base: 0 };
  return {
    base: 0,
    outward: (world) => {
      const [x, y] = outward(worldToLayout(world));
      return [x, -y];
    },
  };
}

export function buildWall(
  builder: MeshBuilder,
  wall: WallRun,
  gates: readonly Gate[],
  wallColor: LinearRGB,
  roofColor: LinearRGB,
  edgeId: number,
  seed: number,
  setting: WallSetting = { isWater: NO_WATER, nicheColor: wallColor }
): WallBuildResult {
  const { path, height, thickness, covered } = wall;
  const anchor = path[0];
  const frame: Frame = { origin: layoutToWorld(anchor, 0), yaw: 0 };
  /* The painter drew the walls, as the towers, several times taller than their survey height, but
     only where they turn their outer face to him (wallExaggeration.ts), so each vertex carries the
     direction to the moat; a wall runs too far for one anchor, so each keeps the field at its own
     foot. */
  const base = { edgeId, seed, strokeAngle: 0, exaggeration: wallExaggeration(setting.outward) };
  const wallStyle: VertexStyle = {
    ...base,
    color: wallColor,
    surface: SURFACE.stone,
    extent: [thickness, height],
  };
  const styles: WallStyles = {
    wall: wallStyle,
    niche: { ...wallStyle, color: setting.nicheColor },
    roof: {
      ...base,
      color: roofColor,
      surface: SURFACE.roofTile,
      extent: [polylineLength(path), 3],
    },
  };
  const local = path.map((p): Vec2 => [p[0] - anchor[0], p[1] - anchor[1]]);
  const intervals = pathIntervals(polylineLength(path), gateCuts(path, gates), WALL.minPieceLength);
  const section = wallSection(height);
  const side = citySide(path, thickness, setting.isWater);
  const before = builder.triangleCount;
  const blocks = localBlocks(gates, anchor);
  const halfWidth = thickness / 2 + (section?.depth ?? 0);
  const pieces = intervals.map(([s0, s1]) => {
    const points = abutGateBlocks(subPath(local, s0, s1), blocks, halfWidth, WALL.gateOverlap);
    const piece: WallPiece = { points, thickness, height, side };
    buildWallPiece(builder, styles, frame, piece, section);
    const depth = section?.depth ?? 0;
    if (covered) {
      const centre = offsetPolyline(piece.points, (side * depth) / 2);
      coveredWalk(builder, styles, frame, centre, height, thickness / 2 + depth / 2);
    }
    return wallPieceRing(piece, section).map((p): Vec2 => [p[0] + anchor[0], p[1] + anchor[1]]);
  });
  return { triangles: builder.triangleCount - before, pieces };
}
